import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'

const RESPONSABLE_LABEL = {
  gimena:  'Gimena',
  nico:    'Nico',
  roberto: 'Roberto',
  otro:    'tu contacto Limpack',
}

const TABS = [
  { path: '/cliente',          icon: '🏠', label: 'Inicio'  },
  { path: '/cliente/tienda',   icon: '🛒', label: 'Tienda'  },
  { path: '/cliente/mi-plan',  icon: '📦', label: 'Mi Plan' },
  { path: '/cliente/cuenta',   icon: '👤', label: 'Cuenta'  },
]

const TABS_DESKTOP = [
  { path: '/cliente',          icon: '🏠', label: 'Inicio'  },
  { path: '/cliente/tienda',   icon: '🛒', label: 'Tienda'  },
  { path: '/cliente/mi-plan',  icon: '📦', label: 'Mi Plan' },
  { path: '/cliente/pedidos',  icon: '📜', label: 'Pedidos' },
  { path: '/cliente/vecinos',  icon: '👥', label: 'Vecinos' },
  { path: '/cliente/soporte',  icon: '💬', label: 'Soporte' },
]

export default function ClienteLayout({ children, title }) {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  const [fueraZonaInfo, setFueraZonaInfo] = useState(null)  // { responsable } o null

  useEffect(() => {
    if (!user?.id) { setFueraZonaInfo(null); return }
    supabase
      .from('clientes')
      .select('fuera_zona, entrega_responsable')
      .or(`titular_id.eq.${user.id},beneficiario_id.eq.${user.id}`)
      .eq('fuera_zona', true)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.fuera_zona) setFueraZonaInfo({ responsable: data.entrega_responsable })
      })
  }, [user?.id])

  function isActive(tabPath) {
    if (tabPath === '/cliente') return path === '/cliente' || path === '/cliente/'
    return path.startsWith(tabPath)
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const initial = (profile?.nombre || user?.email || 'U').charAt(0).toUpperCase()
  const displayName = profile?.nombre?.split(' ')[0] || (user?.email || '').split('@')[0]
  const fullEmail = user?.email || ''

  return (
    <div className="cl-shell">
      {/* ───── Sidebar (desktop) ───── */}
      <aside className="cl-side">
        <Link to="/cliente" className="cl-logo">Lim<span>pack</span></Link>
        <nav className="cl-nav">
          {TABS_DESKTOP.map(t => (
            <Link key={t.path} to={t.path}
              className={`cl-nav-item ${isActive(t.path) ? 'active' : ''}`}>
              <span className="cl-nav-icon">{t.icon}</span>
              <span>{t.label}</span>
            </Link>
          ))}
        </nav>
        <div className="cl-side-footer">
          <Link to="/cliente/cuenta" className="cl-user">
            <div className="cl-avatar">{initial}</div>
            <div className="cl-user-info">
              <div className="cl-user-name">{displayName}</div>
              <div className="cl-user-email">{fullEmail}</div>
            </div>
          </Link>
          <button className="cl-logout" onClick={handleLogout}>Salir</button>
        </div>
      </aside>

      {/* ───── Header (mobile) ───── */}
      <header className="cl-header-mobile">
        <Link to="/cliente" className="cl-logo">Lim<span>pack</span></Link>
        <div className="cl-header-actions">
          {title && <span className="cl-header-title">{title}</span>}
          <button className="cl-icon-btn" onClick={() => navigate('/cliente/cuenta')} aria-label="Cuenta">👤</button>
        </div>
      </header>

      {/* ───── Main ───── */}
      <main className="cl-main">
        {fueraZonaInfo && (
          <div className="cl-fz-banner">
            <span className="cl-fz-icon">🚫</span>
            <div className="cl-fz-text">
              <strong>Tu zona no está en cobertura.</strong> Tu entrega la coordina{' '}
              <strong>{RESPONSABLE_LABEL[fueraZonaInfo.responsable] || 'tu contacto Limpack'}</strong>{' '}
              · sin costo de envío. Cualquier consulta, escribinos por WhatsApp.
            </div>
          </div>
        )}
        {children}
      </main>

      {/* ───── Bottom nav (mobile) ───── */}
      <nav className="cl-bottom">
        {TABS.map(t => (
          <Link key={t.path} to={t.path}
            className={`cl-tab ${isActive(t.path) ? 'active' : ''}`}>
            <span className="cl-tab-icon">{t.icon}</span>
            <span className="cl-tab-label">{t.label}</span>
          </Link>
        ))}
      </nav>

      <style>{`
        :root {
          --cl-navy: #0F1F3D; --cl-navy-2: #1a2a4f;
          --cl-blue: #1a5cb8; --cl-teal: #00C4D4;
          --cl-bg: #f5f7fb; --cl-card: #fff;
          --cl-line: #e2e8f0; --cl-line2: #f1f5f9;
          --cl-muted: #64748b; --cl-muted2: #94a3b8;
          --cl-green: #16a34a;
        }
        .cl-shell {
          min-height: 100vh; background: var(--cl-bg);
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif;
          color: var(--cl-navy);
        }
        .cl-logo { font-weight: 900; font-size: 22px; letter-spacing: -.02em; color: var(--cl-navy); text-decoration: none; display: inline-block }
        .cl-logo span { color: var(--cl-teal) }

        /* ───── Desktop sidebar ───── */
        .cl-side {
          display: none;
          position: fixed; top: 0; left: 0; bottom: 0; width: 240px;
          background: var(--cl-navy); color: #fff;
          padding: 22px 14px; flex-direction: column; z-index: 50;
        }
        .cl-side .cl-logo { color: #fff; padding: 0 8px 24px; font-size: 22px }
        .cl-side .cl-logo span { color: var(--cl-teal) }
        .cl-nav { display: flex; flex-direction: column; gap: 2px; flex: 1 }
        .cl-nav-item {
          display: flex; align-items: center; gap: 12px;
          padding: 11px 14px; border-radius: 10px;
          color: rgba(255,255,255,.7);
          font-size: 14px; font-weight: 600;
          text-decoration: none;
          transition: background .15s, color .15s;
        }
        .cl-nav-item:hover { background: rgba(255,255,255,.06); color: #fff }
        .cl-nav-item.active { background: rgba(0,196,212,.18); color: var(--cl-teal) }
        .cl-nav-icon { font-size: 18px }
        .cl-side-footer { display: flex; flex-direction: column; gap: 8px; margin-top: 12px }
        .cl-user {
          display: flex; align-items: center; gap: 10px; padding: 10px;
          border-radius: 10px; background: rgba(255,255,255,.06);
          text-decoration: none;
        }
        .cl-avatar {
          width: 34px; height: 34px; border-radius: 50%;
          background: linear-gradient(135deg, var(--cl-teal), var(--cl-blue));
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; color: #fff; font-size: 14px;
        }
        .cl-user-info { flex: 1; min-width: 0 }
        .cl-user-name { font-size: 12px; font-weight: 700; color: #fff }
        .cl-user-email { font-size: 10px; color: rgba(255,255,255,.5); white-space: nowrap; overflow: hidden; text-overflow: ellipsis }
        .cl-logout {
          background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.1);
          color: rgba(255,255,255,.85); border-radius: 8px;
          padding: 8px; font-size: 12px; font-weight: 700;
          cursor: pointer; font-family: inherit;
        }
        .cl-logout:hover { background: rgba(255,255,255,.1) }

        /* ───── Mobile header ───── */
        .cl-header-mobile {
          position: sticky; top: 0; z-index: 40;
          background: var(--cl-card); border-bottom: 1px solid var(--cl-line2);
          padding: 14px 16px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .cl-header-mobile .cl-logo { font-size: 18px }
        .cl-header-actions { display: flex; align-items: center; gap: 8px }
        .cl-header-title { font-size: 13px; font-weight: 700; color: var(--cl-muted) }
        .cl-icon-btn {
          width: 34px; height: 34px; border-radius: 50%;
          background: var(--cl-line2); border: 0; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px;
        }

        /* ───── Main ───── */
        .cl-main { padding-bottom: 90px }

        /* Banner fuera de zona */
        .cl-fz-banner {
          margin: 12px 12px 0; padding: 12px 14px;
          background: #FFFBEB; border: 1px solid #FDE68A;
          border-radius: 10px;
          display: flex; align-items: flex-start; gap: 10px;
          font-size: 13px; color: #78350F; line-height: 1.4;
        }
        .cl-fz-icon { font-size: 18px; flex-shrink: 0; line-height: 1.2 }
        .cl-fz-text { flex: 1 }
        .cl-fz-text strong { color: #92400E }

        /* ───── Bottom nav ───── */
        .cl-bottom {
          position: fixed; bottom: 0; left: 0; right: 0;
          background: rgba(255,255,255,.98);
          border-top: 1px solid var(--cl-line);
          display: grid; grid-template-columns: repeat(4, 1fr);
          padding: 6px 4px calc(env(safe-area-inset-bottom, 0) + 16px);
          z-index: 30;
        }
        .cl-tab {
          display: flex; flex-direction: column; align-items: center; gap: 2px;
          padding: 8px 4px; color: var(--cl-muted2);
          text-decoration: none; border-radius: 10px;
          transition: color .15s;
        }
        .cl-tab-icon { font-size: 22px; line-height: 1 }
        .cl-tab-label { font-size: 10px; font-weight: 700 }
        .cl-tab.active { color: var(--cl-teal) }

        /* ───── Desktop breakpoint ───── */
        @media (min-width: 900px) {
          .cl-side { display: flex }
          .cl-header-mobile { display: none }
          .cl-bottom { display: none }
          .cl-main { margin-left: 240px; padding-bottom: 0 }
        }
      `}</style>
    </div>
  )
}
